import { useEffect } from "react";
import { useStudioStore } from "@/store/studio-store";
import { cn } from "@/lib/utils";
import type { DataCategory } from "@/store/types";
import { DataSurface } from "./data-surface";
import { useStudioLocale } from "@/i18n/use-studio-locale";
import { getDataSurfaceCopy } from "@/features/data-explorer/data-surface-copy";

// Identity only — the label comes from the shared `data-surface-copy.ts`, the
// same table `data-surface.tsx` and `data-canvas.tsx` read, so this wrapper
// cannot name a category differently from the navigator underneath it.
const CONNECT_VIEWS: readonly { id: DataCategory }[] = [
  { id: "datasources" },
  { id: "variables" },
];

const CREATOR_DATA_CATEGORIES = ["datasources", "variables"] as const;

export function EssentialsConnectSurface() {
  const dataCategory = useStudioStore((state) => state.dataCategory);
  const setDataCategory = useStudioStore((state) => state.setDataCategory);
  const selectEntity = useStudioStore((state) => state.selectEntity);
  const { locale } = useStudioLocale();
  const copy = getDataSurfaceCopy(locale);

  useEffect(() => {
    setDataCategory("datasources");
    selectEntity(null);
  }, [selectEntity, setDataCategory]);

  const activeCategory = CREATOR_DATA_CATEGORIES.includes(dataCategory as never)
    ? dataCategory
    : "datasources";

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex h-10 shrink-0 items-center border-b border-border px-3">
        <div
          role="tablist"
          aria-label={copy.connectWorkspace}
          className="flex items-center rounded-md bg-muted p-0.5"
        >
          {CONNECT_VIEWS.map((view) => (
            <button
              key={view.id}
              type="button"
              role="tab"
              aria-selected={activeCategory === view.id}
              className={cn(
                "h-7 rounded px-3 text-xs font-medium transition-colors",
                activeCategory === view.id
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
              onClick={() => {
                setDataCategory(view.id);
                selectEntity(null);
              }}
            >
              {copy.categoryLabel[view.id]}
            </button>
          ))}
        </div>
      </div>
      <div className="min-h-0 flex-1">
        <DataSurface visibleCategories={CREATOR_DATA_CATEGORIES} />
      </div>
    </div>
  );
}
